import {Button, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {Dialog2} from "../../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../../misc/SmoothActionBar";
import {useAsyncAction} from "nate-react-api-helpers";
import {api} from "../../../../../api/API";
import {useGetAll} from "../../../../logistics/routes/useGetAll";
import {QuoteOpening} from "../../../../../api/QuoteOpenings";
import {
    AdjustCol,
    DistanceCol,
    NominalWidth,
    Select2Col,
    StringCol,
    Table,
    ViewOnly
} from "../../../../../misc/scroller/Table";
import {useMemoryTableFetcher2} from "../../freeformchanges/products/add/useMemoryTableFetcher";
import {useEffect, useState} from "react";
import {Opening} from "../../../../../api/Openings";
import {doorSeries, frameSeries, labels} from "../../../quote/openings/Const";
import {useProjectUnitSystem} from "../../ProjectName";
import {doorThicknesses, isWindow, openingTypes} from "../../../quote/openings/QuoteOpenings";
import useWindowSize from "react-use/lib/useWindowSize";
import {useSnackbar} from "../../../../../misc/Snackbar";
import {useSyncedRef} from "../../../../../misc/SyncedRef";
import {PopperZIndexProvider} from "../../../../../misc/scroller/PopperZIndexProvider";

type QuoteOpeningExt = (QuoteOpening | Opening) & {
    hwGroupAssign: string;
}

export function BulkUpdateOpenings(props: {
    project: number;
    alternative: number;
    hwGroup: number;
    onClose(): void;
    onUpdated(): void;
}) {

    // @ts-ignore
    const openingReq = useGetAll((input) => {
        if(input.alternative) {
            return api.quoteOpenings.list(input)
        }

        return api.openings.list(input);
    },
        {
            project: props.project,
            alternative: props.alternative || 0,
            hwGroup: props.hwGroup,
            type: "for-hardware-group" as "for-hardware-group",
        }, [props.project, props.alternative, props.hwGroup]);

    const [openings, setOpenings] = useState<QuoteOpeningExt[]>([]);
    const [hasInit, setHasInit] = useState(false);

    const list = openingReq.asList;
    const loading = openingReq.loading
    useEffect(() => {
        if(loading) return;

        setOpenings(list.map(l => ({
            // @ts-ignore
            ...l,
            hwGroupAssign: "No Change",
        })));
        setHasInit(true);
    }, [list, loading]);

    const memory = useMemoryTableFetcher2<QuoteOpeningExt>(openings, setOpenings);
    const win = useWindowSize();

    const snack = useSnackbar();
    const onUpdatedRef = useSyncedRef(props.onUpdated);
    const update = useAsyncAction(async (input) => {
        if(input.alternative) {
            await api.quoteHardware.bulkUpdateOpenings(input);
        } else {
            await api.hardware.bulkUpdateOpenings(input);
        }

        snack.success("Updated");
        onUpdatedRef.current();
    }, []);

    const sys = useProjectUnitSystem();
    if(!sys) return null;

    if(openingReq.loadingOrError || !hasInit) {
        return (
            <Dialog2 open onClose={props.onClose}>
                <DialogTitle>Loading</DialogTitle>
                <DialogContent>
                    {openingReq.LoadingOrErrorElement}
                </DialogContent>
                <DialogActions>
                    <Button onClick={props.onClose}>Cancel</Button>
                </DialogActions>
            </Dialog2>
        )
    }

    const submit = () => {
        update.callback({
            openings: openings,
            hardwareGroup: props.hwGroup,
            project: props.project,
            alternative: props.alternative,
        })
    };

    return (
        <Dialog2 open onClose={props.onClose} fullWidth maxWidth="lg" onEnterPress={submit}>
            <DialogTitle>Bulk Update Openings</DialogTitle>

            <PopperZIndexProvider value={1400}>
                <div style={{
                    height: win.height * 0.75,
                    display: "flex",
                    flexDirection: "column",
                }}>
                    <Table<QuoteOpeningExt>
                        name="hw-bulk-edit-openings"
                        columns={[
                            ViewOnly(StringCol<QuoteOpeningExt>("Opening", "name")),
                            Select2Col<QuoteOpening>("Type", "openingType", openingTypes),
                            NominalWidth<QuoteOpening>("Nominal Width", sys, "nominalWidth", "inactiveDoorWidth"),
                            DistanceCol("Nominal Height", sys, "nominalHeight"),
                            Select2Col<QuoteOpeningExt>("Label", "label", labels),
                            Select2Col<QuoteOpeningExt>("Frame Series", "frameSeries", frameSeries),
                            AdjustCol(Select2Col<QuoteOpening>("Door Thickness", "doorThickness", doorThicknesses(sys)), {
                                disabled: isWindow as any,
                            }),
                            AdjustCol(Select2Col<QuoteOpening>("Door Series", "doorSeries", doorSeries), {
                                disabled: isWindow as any,
                            }),
                            Select2Col<QuoteOpeningExt>("Hw Group", "hwGroupAssign", groupDesignators),
                        ]}
                        fetch={ctx => memory.fetch()}
                        fetchDeps={[]}
                        onChange={v => memory.onChange(v)}
                    />
                </div>
            </PopperZIndexProvider>

            <SmoothActionBar
                loading={update.loading}
                error={update.error}
                onCancel={props.onClose}
                onSubmit={submit}
                actionName="Update"
            />
        </Dialog2>
    )
}

const groupDesignators = ["No Change", "New Group #A", "New Group #B", "New Group #C"]